import {Icon} from '../ui/Icon';
import { useEffect, useMemo, useState } from 'react';
import { Link } from '../ui/ScopedLink';
import { api } from '../api';
import { formatDateOnly } from '../dates';
import {DataTable} from '../ui/DataTable';
import {StatusBadge} from '../ui/StatusBadge';

type DocumentSummary = {
  id: string;
  title: string;
  document_number: string;
  document_type_code: string;
  document_type_name: string;
  category_code: string;
  lifecycle_status: string;
  verification_status: string;
  inspection_date?: string;
  effective_date?: string;
  expiry_date?: string;
  review_date?: string;
  expiry_state: string;
};

type KeyDocumentDefinition = {
  key: string;
  label: string;
  hint: string;
  documentType?: string;
  matches: (document: DocumentSummary) => boolean;
};

const definitions: KeyDocumentDefinition[] = [
  { key: 'insurance', label: 'Buildings insurance', hint: 'Current policy and schedule', documentType: 'BUILDINGS_INSURANCE', matches: document => document.document_type_code === 'BUILDINGS_INSURANCE' },
  { key: 'fra', label: 'Fire risk assessment', hint: 'Latest FRA', documentType: 'FRA', matches: document => document.document_type_code === 'FRA' || /fire risk assessment|\bfra\b/i.test(`${document.document_type_name} ${document.title}`) },
  { key: 'eicr', label: 'Electrical installation report', hint: 'Latest communal EICR', matches: document => /\beicr\b|electrical installation condition/i.test(`${document.document_type_name} ${document.title}`) },
  { key: 'fire-doors', label: 'Fire-door survey', hint: 'Latest communal fire-door record', matches: document => /fire.?door/i.test(`${document.document_type_name} ${document.title}`) },
  { key: 'asbestos', label: 'Asbestos', hint: 'Register or latest survey', matches: document => document.category_code === 'ASBESTOS' || /asbestos/i.test(`${document.document_type_name} ${document.title}`) },
  { key: 'emergency-lighting', label: 'Emergency lighting', hint: 'Latest inspection or service evidence', matches: document => /emergency light/i.test(`${document.document_type_name} ${document.title}`) },
  { key: 'lift', label: 'Lift examination', hint: 'Latest LOLER or thorough examination', documentType: 'LOLER_REPORT', matches: document => document.document_type_code === 'LOLER_REPORT' || /\bloler\b|lift.*(report|examination|inspection)/i.test(`${document.document_type_name} ${document.title}`) },
];

function documentDate(document: DocumentSummary): number {
  const value = document.inspection_date ?? document.effective_date ?? document.review_date ?? document.expiry_date;
  const parsed = value ? Date.parse(value) : 0;
  return Number.isNaN(parsed) ? 0 : parsed;
}

function displayDate(document: DocumentSummary): string {
  const value = document.inspection_date ?? document.effective_date ?? document.review_date ?? document.expiry_date;
  return formatDateOnly(value,'Date not recorded');
}

function state(document: DocumentSummary): { label: string; warning: boolean } {
  if (document.expiry_state === 'Expired') return { label: 'Expired', warning: true };
  if (document.expiry_state === 'Expiring soon') return { label: 'Due soon', warning: true };
  if (document.verification_status !== 'Verified') return { label: 'Needs review', warning: true };
  return { label: 'Current', warning: false };
}

export function KeyDocuments({ developmentId }: { developmentId: string }) {
  const [documents, setDocuments] = useState<DocumentSummary[]>();
  const [error, setError] = useState('');
  useEffect(() => {
    const controller=new AbortController();setDocuments(undefined);setError('');
    void api<DocumentSummary[]>(`/api/documents?developmentId=${developmentId}&keyDocuments=true&limit=200`,{signal:controller.signal})
      .then(rows=>{if(!controller.signal.aborted)setDocuments(rows)})
      .catch(reason=>{if(!controller.signal.aborted)setError(reason instanceof Error?reason.message:String(reason))});
    return()=>controller.abort();
  }, [developmentId]);
  const current = useMemo(() => (documents ?? []).filter(document => !['Archived', 'Withdrawn', 'Superseded'].includes(document.lifecycle_status)), [documents]);

  return <section className="panel building-section key-documents">
    <div className="section-heading"><div><h2>Key documents</h2><p>Open the current building-critical record without searching the full repository.</p></div><Link to={`/documents?developmentId=${developmentId}`}>Open all building documents <Icon name="arrow-right"/></Link></div>
    {error && <p className="error">{error}</p>}
    {!documents&&!error?<p className="muted">Loading key documents…</p>:documents&&<DataTable caption={<span className="sr-only">Current key building documents</span>} scrollLabel="Key building documents" rows={definitions} getRowKey={row=>row.key}
 rowHeader={{id:'type',header:'Document',render:row=><span title={row.hint}>{row.label}</span>}}
 columns={[
 {id:'record',header:'Current record',render:definition=>{const document=current.filter(definition.matches).sort((a,b)=>documentDate(b)-documentDate(a))[0];
return document?<Link to={'/documents/'+document.id}>{document.title}</Link>:<Link to={definition.documentType?`/documents?developmentId=${developmentId}&documentType=${definition.documentType}`:`/documents?developmentId=${developmentId}&q=${encodeURIComponent(definition.label)}`}>Search or add record</Link>}},
 {id:'date',header:'Recorded date',render:definition=>{const document=current.filter(definition.matches).sort((a,b)=>documentDate(b)-documentDate(a))[0];return document?displayDate(document):'Not recorded'}},
 {id:'status',header:'Evidence status',render:definition=>{const document=current.filter(definition.matches).sort((a,b)=>documentDate(b)-documentDate(a))[0];return <StatusBadge text={document?state(document).label:'Not recorded'}/>}},
 ]}/>}

  </section>;
}
